import React, { useCallback, useMemo } from 'react';
import { Checkbox, Col, Grid, Row } from 'rsuite';
import { H6 } from '@/widgets/components/title-styles';
import styled from 'styled-components';
import {
  InspectionQuestionSection,
  InspectionResult,
  PropertyContacts,
} from '../../api/ariadne/__generated_types__/graphql';
import {
  getQuestionResult,
  getQuestionResultColor,
} from './question-result-helpers';
import {
  INSPECTION_FAILED_STATUS,
  INSPECTION_SUCCESS_STATUS,
} from '../constants';
import { Operand, Question } from './types';

const InspectionWrapper = styled.section`
  margin-top: 12px;
`;

const InspectionTitle = styled(H6)`
  border-bottom: 1px solid #999;
  padding-bottom: 8px;
  margin-bottom: 8px;
`;

const QuestionHeader = styled.dt`
  font-size: 12px;
  font-weight: normal;
`;

const QuestionBody = styled.dd`
  font-size: 12px;
  margin: 8px 0 12px;
`;

const NotesTitle = styled.div`
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const NotesText = styled.div`
  font-size: 12px;
  margin-bottom: 8px;
`;

const ResultCol = styled(Col)`
  color: ${(props) => (props.color ? props.color : '')};
  font-size: 12px;
  text-align: right;
`;

const CheckboxWrapper = styled(Checkbox)`
  font-size: 13px;
`;

interface InspectionSectionProps {
  configQuestion?: InspectionQuestionSection;
  results: any;
  showPass: boolean;
  inspectionResult?: InspectionResult;
  apnData?: PropertyContacts;
}

export const InspectionSection = ({
  configQuestion,
  results,
  showPass,
  inspectionResult,
  apnData,
}: InspectionSectionProps) => {
  const inspectionIsNotInProgress =
    inspectionResult === INSPECTION_SUCCESS_STATUS ||
    inspectionResult === INSPECTION_FAILED_STATUS;

  const getVisibility = useCallback(
    (question: Question): boolean => {
      if (question.is_visible === undefined || question.is_visible === null) {
        // Visible by default
        return true;
      }

      // Iterate over the operands
      return question.is_visible.operands.reduce(
        (prev: boolean, operand: Operand) => {
          const targetQuestion = configQuestion?.questions.find(
            (q: Question) => q.id === operand.question_id
          );
          // If there's no target question the value is false
          if (!targetQuestion) {
            return false;
          }

          if (targetQuestion.question_type === 'boolean') {
            const labels = targetQuestion.question_labels;
            if (labels) {
              // If it has labels the comparison has to be made by label
              return results[operand.question_id] === labels.fail && prev;
            }
            // If no labels the assumption is this comes as a boolean
            return results[operand.question_id] && prev;
          }
          // If no supported type it is assumed the question has to be shown.
          return true;
        },
        true
      );
    },
    [results, configQuestion]
  );

  const filteredResults = useMemo(() => {
    if (showPass || !inspectionIsNotInProgress)
      return configQuestion?.questions;

    return configQuestion?.questions.filter((question: Question) => {
      const result = results ? results[question.id] || null : null;
      const visible = getVisibility(question);
      if (!visible) {
        return false;
      } else if (result) {
        return result.toLowerCase() !== 'pass';
      }
      return true;
    });
  }, [showPass, configQuestion, results]);

  return (
    <InspectionWrapper>
      <InspectionTitle>{configQuestion?.title}</InspectionTitle>
      {inspectionIsNotInProgress &&
        configQuestion?.optional &&
        results.skipped && (
          <CheckboxWrapper checked disabled>
            This section not applicable
          </CheckboxWrapper>
        )}
      {inspectionIsNotInProgress &&
        (!configQuestion?.optional || !results.skipped) && (
          <Grid fluid>
            {filteredResults.map((question: any) => {
              const resultText = getQuestionResult(
                question,
                inspectionResult,
                results[question.id],
                apnData
              );
              const resultColor = getQuestionResultColor(results[question.id]);

              return (
                <Row key={question.id}>
                  <Col xs={20}>
                    <dl>
                      <QuestionHeader
                        dangerouslySetInnerHTML={{
                          __html: question.header_html,
                        }}
                      />
                      <QuestionBody
                        dangerouslySetInnerHTML={{ __html: question.body_html }}
                      />
                    </dl>
                  </Col>
                  <ResultCol xs={4} color={resultColor}>
                    {resultText}
                  </ResultCol>
                </Row>
              );
            })}
            {results?.notes && (
              <Row>
                <Col xs={24}>
                  <NotesTitle>{configQuestion?.notes?.title}</NotesTitle>
                  <NotesText>{results?.notes}</NotesText>
                </Col>
              </Row>
            )}
          </Grid>
        )}
    </InspectionWrapper>
  );
};
